import { format } from "date-fns";
import { TFunction, i18n as I18n } from "i18next";
import { ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  VacancySyncState,
  type VacancySyncOverviewDto,
  type VacancySyncStateApiValue,
} from "../../api/types";

import { Badge } from "@/components/ui/v2/badge";
import { ROUTES } from "@/constants/routes";

function formatDate(dateString: string | null) {
  if (!dateString) return null;
  try {
    return format(new Date(dateString), "PPp");
  } catch {
    return dateString;
  }
}

function lowerFirst(value: string): string {
  return value.length > 0 ? value[0].toLowerCase() + value.slice(1) : value;
}

function getSyncStateKey(state: VacancySyncStateApiValue): string | null {
  if (typeof state === "number") {
    const stateName = VacancySyncState[state];
    return typeof stateName === "string" ? lowerFirst(stateName) : null;
  }

  const trimmedState = state.trim();
  if (!trimmedState) return null;

  // Backend can return enum name strings in some environments (e.g. "Synced").
  return lowerFirst(trimmedState);
}

/**
 * Creates all custom cell renderers for Indeed vacancy sync table columns.
 * Returns a map of column keys to renderer functions.
 */
export function createVacancySyncCellRenderers(
  t: TFunction,
  i18n: I18n,
  clientId?: string
): Record<string, (value: unknown, row: unknown) => ReactNode> {
  const renderers: Record<string, (value: unknown, row: unknown) => ReactNode> = {};

  // VacancyId renderer - shows deleted badge
  renderers.vacancyId = (value: unknown, row: unknown): ReactNode => {
    const item = row as VacancySyncOverviewDto;
    return (
      <div className="flex items-center gap-2">
        <span
          className={
            item.isDeleted ? "line-through text-muted-foreground" : ""
          }
        >
          {String(value ?? "")}
        </span>
        {item.isDeleted && (
          <Badge variant="archive" size="small">
            {t("integrations.indeed.jobSync.syncOverview.columns.deleted")}
          </Badge>
        )}
      </div>
    );
  };

  // VacancyTitle renderer - shows deleted styling
  renderers.vacancyTitle = (value: unknown, row: unknown): ReactNode => {
    const item = row as VacancySyncOverviewDto;
    return (
      <span
        className={item.isDeleted ? "line-through text-muted-foreground" : ""}
      >
        {String(value) ||
          t("integrations.indeed.jobSync.syncOverview.vacancyTitleFallback", {
            id: item.vacancyId,
          })}
      </span>
    );
  };

  // SyncState renderer - translates sync state
  renderers.syncState = (_value: unknown, row: unknown): ReactNode => {
    const item = row as VacancySyncOverviewDto;
    if (item.syncState === null) return <span>-</span>;

    const syncStateKey = getSyncStateKey(item.syncState);
    if (!syncStateKey) return <span className="text-muted-foreground">-</span>;

    const translationKey = `integrations.indeed.jobSync.vacancies.states.${syncStateKey}`;
    return (
      <span>
        {i18n.exists(translationKey)
          ? t(translationKey)
          : syncStateKey}
      </span>
    );
  };

  // IssueDescriptionKey renderer - translates issue description
  renderers.issueDescriptionKey = (value: unknown): ReactNode => {
    return <span>{t(String(value))}</span>;
  };

  // PublicationDate renderer - formats date
  renderers.publicationDate = (value: unknown): ReactNode => {
    const formattedDate = value ? formatDate(String(value)) : null;
    return (
      <span className="text-muted-foreground">
        {formattedDate || t("integrations.indeed.jobSync.syncOverview.noDate")}
      </span>
    );
  };

  // SegmentName renderer - creates link to segment
  renderers.segmentName = (_value: unknown, row: unknown): ReactNode => {
    const item = row as VacancySyncOverviewDto;
    const segmentLink =
      item.segmentId && clientId && !item.isSegmentArchived
        ? `/${ROUTES.clients}/${clientId}/${ROUTES.segments}/${item.segmentId}`
        : null;
    return item.segmentName ? (
      segmentLink ? (
        <Link to={segmentLink} className="text-primary hover:underline">
          {item.segmentName}
        </Link>
      ) : (
        <span>{item.segmentName}</span>
      )
    ) : (
      <span>-</span>
    );
  };

  // ExceptionCode renderer - translates exception codes
  renderers.exceptionCode = (_value: unknown, row: unknown): ReactNode => {
    const item = row as VacancySyncOverviewDto;
    return (
      <span className="text-muted-foreground">
        {item.exceptionCode
          ? i18n.exists(
            `integrations.indeed.jobSync.vacancies.exceptionCodes.${item.exceptionCode}`
          )
            ? t(
              `integrations.indeed.jobSync.vacancies.exceptionCodes.${item.exceptionCode}`
            )
            : t("exceptionCodes.unknownError")
          : "-"}
      </span>
    );
  };

  return renderers;
}
